<template>
  <h1 class="title">Homicidios y desapariciones de jóvenes por región</h1>
  <p class="pie-burbujas">
    El gráfico interactivo compara las tasas de homicidio y desaparición por
    cada cien mil jóvenes en cada región del país desde 2006.
  </p>
  <div class="controlesRegionales">
    <label>Selecciona el fenómeno que desees visualizar </label>
    <select v-model="seleccion">
      <option>Desapariciones</option>
      <option>Homicidios</option>
    </select>
  </div>

  <div class="contenedor">
    <div class="tarjeta-mapa">
      <img src="../assets/regiones.png" class="mapa" />
    </div>
    <div id="contenedor-bubbles" class="graf-bubbles">
      <div id="tooltipRegional">
        Region: <span class="highlight">{{ region_tooltip }} </span><br />
        Año: <span class="highlight">{{ anio_tooltip }} </span><br />
        Tasa: <span class="highlight">{{ tasa_tooltip }}</span> por cada cien
        mil
      </div>
      <svg
        id="grafica-burbujas"
        :width="ancho + margenes.derecha + margenes.izquierda"
        :height="alto_svg"
      >
        <g
          class="eje-x"
          :transform="`translate(${margenes.izquierda},${+margenes.arriba})`"
        ></g>
        <g
          class="eje-y"
          :transform="`translate(${margenes.izquierda},${margenes.arriba})`"
        ></g>
        <g
          class="grupo-burbujas"
          :transform="`translate(${margenes.izquierda},${margenes.arriba})`"
        ></g>
      </svg>
    </div>
  </div>

  <p class="instrucciones">
    Los valores de esta gráfica están redondeados. <br />
    <span class="highlight">Fuente</span>: Elaboración propia a partir de datos
    del INEGI y del RNPDNO.
  </p>
</template>
<script setup>
import * as d3 from "d3";
import { onMounted, ref, onUnmounted, watch } from "vue";
import tasas_formateadas from "@/assets/tasas_formateadas.json";

const data = ref(tasas_formateadas.desapariciones);
const seleccion = ref("Homicidios");
const regiones = ref(["noreste", "noroeste", "occidente", "centro", "sureste"]);
const color_dict = {
  noreste: "#F4CB7C",
  noroeste: "#619897",
  centro: "#C1473B",
  occidente: "#382633",
  sureste: "#40476D",
};
const contenedor_svg = ref();
const svg = ref();
const grupo_burbujas = ref();
const ancho = ref(0);
const alto_svg = ref(600);
const alto = ref(0);
const margenes = ref({
  derecha: 20,
  izquierda: 55,
  abajo: 50,
  arriba: 40,
});
const texto_x = ref();
const texto_y = ref();

const formatoTemporal = ref(d3.timeParse("%Y"));
const escalaTemporal = ref();
const escalaRegional = ref();
const escalaRadio = ref();
const eje_x = ref();
const eje_y = ref();
const radio_minimo = ref();
const radio_maximo = ref();

const tooltipRegional = ref();
const anio_tooltip = ref();
const region_tooltip = ref();
const tasa_tooltip = ref();

onMounted(() => {
  //console.log(data.value);
  setData();
  contenedor_svg.value = document.getElementById("contenedor-bubbles");
  svg.value = d3.select("svg#grafica-burbujas");
  grupo_burbujas.value = svg.value.select("g.grupo-burbujas");
  eje_x.value = svg.value.select("g.eje-x");
  eje_y.value = svg.value.select("g.eje-y");
  texto_x.value = svg.value.append("text").attr("text-anchor", "end");
  texto_y.value = svg.value.append("text").attr("text-anchor", "end");

  tooltipRegional.value = d3.select("div#tooltipRegional");

  redimensionanding();
  dibujandoBurbujas();
  window.addEventListener("resize", reescalanding);
});
onUnmounted(() => {
  window.removeEventListener("resize", reescalanding);
});
watch(seleccion, () => {
  //console.log(seleccion.value);
  setData();
  //console.log(data.value);
  redimensionanding();
  dibujandoBurbujas();
});

function setData() {
  if (seleccion.value == "Desapariciones") {
    data.value = tasas_formateadas.desapariciones;
    radio_minimo.value = 0;
    radio_maximo.value = 25;
  } else {
    data.value = tasas_formateadas.homicidios;
    radio_minimo.value = 2;
    radio_maximo.value = 45;
  }
  console.log(radio_minimo.value, radio_maximo.value);
}
function reescalanding() {
  redimensionanding();
  dibujandoBurbujas();
}
function redimensionanding() {
  ancho.value =
    contenedor_svg.value.clientWidth -
    margenes.value.derecha -
    margenes.value.izquierda;
  alto.value = alto_svg.value - margenes.value.arriba - margenes.value.abajo;
  escalaRegional.value = d3
    .scaleBand()
    .domain(regiones.value)
    .range([0, ancho.value]);
  escalaTemporal.value = d3
    .scaleTime()
    .domain(d3.extent(data.value.map((d) => formatoTemporal.value(d.anio))))
    .range([0, alto.value]);
  escalaRadio.value = d3
    .scaleLinear()
    .domain(d3.extent(data.value.map((d) => d.value)))
    .range([radio_minimo.value, radio_maximo.value]);
  eje_x.value.call(d3.axisTop(escalaRegional.value));
  eje_y.value.call(d3.axisLeft(escalaTemporal.value).ticks(17));

  texto_x.value
    .attr("x", ancho.value / 2 + margenes.value.izquierda)
    .attr("y", 10)
    .text("Región")
    .attr("font-size", "14")
    .attr("font-weight", "bold");

  texto_y.value
    .attr("transform", "rotate(-90)")
    .attr("y", 13)
    .attr("x", -alto.value / 2)
    .attr("font-size", "14")
    .text("Año")
    .attr("font-weight", "bold");
}

function dibujandoBurbujas() {
  // Agregamos los círculos
  grupo_burbujas.value
    .selectAll("circle")
    .data(data.value)
    .join(
      (enter) => {
        enter
          .append("circle")
          .attr("class", "circulo")
          .attr(
            "cx",
            (d) =>
              escalaRegional.value(d.region) +
              escalaRegional.value.bandwidth() / 2
          )
          .attr("cy", (d) =>
            escalaTemporal.value(formatoTemporal.value(d.anio))
          )
          .attr("r", (d) => escalaRadio.value(d.value))
          .attr("fill", (d) => color_dict[d.region])
          .attr("fill-opacity", 0.85);
      },
      (update) => {
        update
          .transition()
          .attr(
            "cx",
            (d) =>
              escalaRegional.value(d.region) +
              escalaRegional.value.bandwidth() / 2
          )
          .attr("cy", (d) =>
            escalaTemporal.value(formatoTemporal.value(d.anio))
          )
          .attr("r", (d) => escalaRadio.value(d.value))
          .attr("fill", (d) => color_dict[d.region]);
      },
      (exit) => {
        exit.remove();
      }
    );
  grupo_burbujas.value
    .selectAll(".circulo")
    .on("mouseover", abrirTooltip)
    .on("mousemove", ajustarPosicionTooltip)
    .on("mouseleave", cerrarTooltip);

  tooltipRegional.value.attr("class", "tultip").style("visibility", "hidden");
}

var abrirTooltip = function () {
  let currentRegion = d3.select(this).datum().region;
  let currentAnio = d3.select(this).datum().anio;
  let currentTasa = d3.select(this).datum().value;

  anio_tooltip.value = currentAnio;
  region_tooltip.value =
    currentRegion.charAt(0).toUpperCase() + currentRegion.slice(1);
  tasa_tooltip.value = currentTasa;

  //console.log("se abrio tooltip");
  //console.log(currentAnio, currentRegion, currentTasa);
  tooltipRegional.value.style("visibility", "visible");
};

var ajustarPosicionTooltip = function () {
  //console.log(d3.pointer(event, document.body));

  let x_position = 0;
  if (d3.pointer(event, document.body)[0] + 75 > ancho.value - 50) {
    x_position = d3.pointer(event, document.body)[0] - 110;
  } else {
    x_position = d3.pointer(event, document.body)[0] + 30;
  }
  tooltipRegional.value
    .style("left", x_position + "px")
    .style("top", d3.pointer(event, document.body)[1] + "px");
};
var cerrarTooltip = function () {
  tooltipRegional.value.style("visibility", "hidden");
};
</script>
<style scoped>
.title {
  margin-bottom: 3px;
}
.pie-burbujas {
  margin-top: 5px;
  margin-left: 10px;
  text-align: left;
}
.instrucciones {
  text-align: left;
  font-size: 12px;
  margin-left: 10px;
  margin-top: 2px;
}
.highlight {
  font-weight: bold;
}
.contenedor {
  display: flex;
  flex-wrap: wrap;
}
.tarjeta-mapa {
  display: flex;
  justify-content: center;
  width: 35%;
  height: 600px;
}
.graf-bubbles {
  width: 65%;
}
.mapa {
  width: 100%;
  opacity: 0.8;
  /*margin-top: 10px; */
  margin: auto;
}
.tultip {
  position: absolute;
  z-index: 2;
  background-color: #252323;
  color: white;
  opacity: 0.93;
  height: auto;
  width: 100px;
  font-size: 14px;
  padding: 5px;
}

.controlesRegionales {
  margin-bottom: 10px;
}
@media (max-width: 769px) {
  .tarjeta-mapa {
    width: 100%;
    height: auto;
  }
  .graf-bubbles {
    width: 100%;
  }
}
</style>
